import {
  WppIconChevron,
  WppIconDownload,
  WppIconExternalLink,
  WppListItem,
  WppMenuContext,
} from '@wppopen/components-library-react'
import { AnalyticsActionType } from '@wppopen/core'
import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import ElipsisIcon from 'assets/icons/elipsis.svg'
import ProjectsListDetailedView from 'components/projectsListDetailedView/ProjectsListDetailedView'
import StatusNotification from 'components/statusNotification/StatusNotification'
import TruncatedText from 'components/truncatedText/TruncatedText'
import briefErrorMapper from 'helpers/briefErrorMapper'
import downloadFiles from 'helpers/downloadFiles'
import { parseStatusCode } from 'helpers/projectStatusHelper'
import { Project, SyncStatus } from 'types/Project'
import { formatDate } from 'utils'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './ProjectsList.module.scss'
import { TableSort } from './TableSort'

export interface Header {
  title?: string | JSX.Element
  key: string
  sortable?: boolean
  sortingKey?: string
  className?: string
  surtitle?: string
  group?: string
  hideBorder?: boolean
}

interface Props {
  headers: Header[]
  items?: Project[]
  className?: string
  onHeaderClick?: (T: Sort) => void
  isLoading?: boolean
  onFiltersClear?: () => void
  hasFilters?: boolean
  sort?: Sort
}

export interface Sort {
  orientation?: 'ASC' | 'DESC' | undefined
  sortingKey?: string
}

const queryKeys = {
  BRIEF_ID: 'briefId',
  ID: 'id',
  WORKFRONT_PROJECT_ID: 'workfrontProjectId',
}

const ProjectsList = ({ headers, items, className, onHeaderClick, sort, isLoading }: Props) => {
  const [query] = useSearchParams()
  const [expanded, setExpanded] = useState<undefined | number>()

  const expandedRef = useRef<HTMLDivElement>(null)

  const hasInitialized = useRef(false)
  const hasScrolled = useRef(false)

  useEffect(() => {
    if (hasInitialized.current || !items) return

    const workfrontId = query.get(queryKeys.WORKFRONT_PROJECT_ID)
    const index = items.findIndex(i => i.workfrontProjectId === workfrontId)
    if (index !== -1) {
      setExpanded(index)
    }
    hasInitialized.current = true
  }, [items, query])

  useEffect(() => {
    if (!hasScrolled.current && expandedRef.current) {
      expandedRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    hasScrolled.current = false
  }, [expanded])

  const handleExpandRow = (index: number) => (index === expanded ? setExpanded(undefined) : setExpanded(index))

  const handleSortClick = (header: Header) => {
    if (!header.sortable) return

    const currentOrientation =
      sort?.orientation === undefined ? 'DESC' : sort?.orientation === 'DESC' ? 'ASC' : undefined

    onHeaderClick && onHeaderClick({ sortingKey: header.sortingKey, orientation: currentOrientation })
    setExpanded(undefined)
  }

  return (
    <div className={clsx(styles.practitionersList, className)}>
      <div className={styles.aboveHeader}>
        <div />
        <div className={clsx(styles.borderR)}>
          <span className="invisible">*</span>{' '}
        </div>
        <div className={clsx('col-span-3 text-center', styles.headerGroupTitle, styles.borderR)}>STATUS</div>
        <div />
      </div>
      <div className={styles.header}>
        {headers?.map((header, index) => (
          <div
            key={`header-${index}`}
            className={clsx('relative', styles.headerItem, header?.className, {
              [styles.noBorder]: header?.hideBorder,
            })}
            onClick={() => handleSortClick(header)}
          >
            <span className="max-w-[85%]">{header.title}</span>
            {header.sortable && (
              <div className="absolute right-1">
                <TableSort orientation={header.sortingKey === sort?.sortingKey ? sort?.orientation : undefined} />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className={styles.list}>
        {isLoading ? (
          <div className={styles.noData}>Loading, please wait.</div>
        ) : items?.length ? (
          items.map((project, index, array) => {
            const {
              workfrontProjectId,
              name,
              briefType,
              status,
              operatingUnit,
              targetInMarketDate,
              expectedDueDate,
              uhubResponse,
              createdAt,
              uhubStatus,
              uhubProjectStatus,
              syncStatus,
              documentUrls,
            } = project
            return (
              <React.Fragment key={index}>
                <div className={styles.row} key={`row-${index}`} ref={expanded === index ? expandedRef : null}>
                  <div
                    className={clsx(styles.rowItem, styles.centered, 'cursor-pointer')}
                    onClick={() => {
                      handleExpandRow(index)
                      trackAnalytics({
                        type: AnalyticsActionType.action,
                        payload: ANALYTICS_EVENTS.OVERVIEW_ITEM_EXPANDED(name),
                      })
                    }}
                  >
                    <WppIconChevron
                      className={clsx(styles.centered, styles.chevron, 'rotate-90 transition-all', {
                        [styles.chevronOpen]: index === expanded,
                      })}
                      color="black"
                    />
                  </div>
                  {/* Name & briefType */}
                  <div className={clsx(styles.rowItem, 'whitespace-nowrap')}>
                    <div
                      className={clsx('flex w-full items-center', styles.nameInactive, {
                        [styles.syncError]: syncStatus === SyncStatus.ERROR,
                      })}
                    >
                      <StatusNotification
                        statusType={syncStatus}
                        message={briefErrorMapper(uhubResponse)}
                        className="mr-2"
                      />
                      <div className="text-[16px]">
                        <span className="font-semibold">
                          <TruncatedText text={name} maxWidth="300px" />
                        </span>
                        <br />
                        <span className={styles.briefType}> {briefType}</span>
                      </div>
                    </div>
                  </div>

                  {/* Status - Workfront submission */}
                  <div className={clsx(styles.rowItem, styles.statusTag)}>
                    {status && (
                      <div
                        className={styles.statusTagInner}
                        style={{ background: `${parseStatusCode(status)?.bgColor}` }}
                      >
                        <span style={{ color: parseStatusCode(status)?.labelColor }}>
                          {parseStatusCode(status)?.label}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className={clsx(styles.rowItem, styles.statusTag)}>
                    {uhubProjectStatus && (
                      <div
                        className={styles.statusTagInner}
                        style={{ background: `${parseStatusCode(uhubProjectStatus, uhubStatus)?.bgColor}` }}
                      >
                        <span style={{ color: parseStatusCode(uhubProjectStatus, uhubStatus)?.labelColor }}>
                          {parseStatusCode(uhubProjectStatus, uhubStatus)?.label}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Phase - uHub */}
                  <div className={clsx(styles.rowItem)}>
                    {uhubStatus && (
                      <div>
                        <span>{uhubStatus}</span>
                      </div>
                    )}
                  </div>

                  {/* Operating unit */}
                  <div className={clsx(styles.rowItem)}>{operatingUnit}</div>

                  {/* Date submitted */}
                  <div className={clsx(styles.rowItem)}>{formatDate(createdAt)}</div>

                  {/* Target in market date */}
                  <div className={clsx(styles.rowItem)}>{formatDate(targetInMarketDate || expectedDueDate)}</div>

                  {/* Briefd document */}
                  <div className={clsx(styles.rowItem, 'cursor-pointer justify-center items-center')}>
                    <WppMenuContext slot="trigger-element">
                      <div slot="trigger-element" className="ml-4">
                        <img src={ElipsisIcon} alt="elipsis" />
                      </div>
                      <div>
                        <WppListItem onWppChangeListItem={() => downloadFiles(documentUrls?.filter(url => !!url)?.[0])}>
                          <WppIconDownload color="black" slot="left" />
                          <p slot="label">Download Brief</p>
                        </WppListItem>
                        <WppListItem
                          disabled={!uhubResponse?.key}
                          onWppChangeListItem={() => {
                            window.open(`${process.env.REACT_APP_UHUB_URL}/browse/${uhubResponse?.key}`, '_newtab')
                            trackAnalytics({
                              type: AnalyticsActionType.action,
                              payload: ANALYTICS_EVENTS.DETAILED_ITEM_UHUB_BUTTON_CLICKED(name),
                            })
                          }}
                        >
                          <WppIconExternalLink slot="left" />
                          <p slot="label">Go to UHUB</p>
                        </WppListItem>
                        <WppListItem
                          onWppChangeListItem={() => {
                            window.open(
                              `${process.env.REACT_APP_WORKFRONT_URL}/project/${workfrontProjectId}`,
                              '_blank',
                            )
                            trackAnalytics({
                              type: AnalyticsActionType.action,
                              payload: ANALYTICS_EVENTS.DETAILED_ITEM_WORKFRONT_BUTTON_CLICKED(name),
                            })
                          }}
                        >
                          <WppIconExternalLink slot="left" />
                          <p slot="label">Go to Workfront</p>
                        </WppListItem>
                      </div>
                    </WppMenuContext>
                  </div>
                  <ProjectsListDetailedView isOpen={expanded === index} project={project} />
                </div>
                {index < array?.length - 1 && (
                  <div key={`divider-${index}`} className={clsx(styles.rowDivider, 'col-span-10')} />
                )}
              </React.Fragment>
            )
          })
        ) : (
          <div className={styles.noData}>No data.</div>
        )}
      </div>
    </div>
  )
}

export default ProjectsList
